import { z } from 'zod';
import { StatusCategoryEnum } from './status.schema';

// ─── Global Search (ADR 0011 D4, Phase 6d) ───
//
// One endpoint — GET /api/v1/search — over Task (title/description), List, Folder,
// Space (name), and TaskComment (content). Results are grouped top-N per type with a
// per-type cursor. Every query path is scope-filtered server-side through the same
// space-reachability predicate (accessibleSpaceWhere; comments via task→list→space),
// so this contract carries NO scope inputs — scope-safety is inherited, not requested.
//
// SQL Server note (ADR 0011 D1): Prisma's `mode: 'insensitive'` is UNSUPPORTED on SQL
// Server. Plain `contains` / `startsWith` already match case-insensitively under the
// database's default collation, so the service uses them directly with no mode flag.
//
// Ranking v1 (ADR 0011 D1): exact-prefix boost via a TWO-PHASE keyset cursor (NP-143).
// Per type, two DISJOINT phases, each ordered `updatedAt desc, id desc`:
//   phase P — `startsWith(q)` prefix hits;  phase C — `contains(q)` AND NOT `startsWith(q)`.
// A page fills from P first (prefix matches always rank above substring matches), spilling
// into C once P is exhausted. The opaque cursor encodes the phase ('p'|'c') plus the
// (updatedAt, id) tuple, so a cursored page resumes the correct phase. Because the phases are
// query-level disjoint and each is independently keyset-complete, every row is delivered
// EXACTLY once — no page-boundary drop (the pre-NP-143 single-phase bug) and no duplicate.
// Cursor is opaque (see meta.nextCursor).

/** Entity types searchable via the global endpoint. */
export const SearchTypeEnum = z.enum(['task', 'list', 'folder', 'space', 'comment']);

/**
 * CSV of search types ("task,comment") → validated SearchType[].
 * Matches the CSV multi-value convention used by TaskFilterSchema (query-string params
 * arrive as single strings). Blank entries rejected; 1–5 distinct-ish values.
 */
const csvSearchTypes = z
  .string()
  .transform((s) => s.split(',').map((v) => v.trim()))
  .pipe(z.array(SearchTypeEnum).min(1).max(5));

/**
 * Query params for GET /api/v1/search.
 * @example
 * const q = SearchQuerySchema.parse({ q: 'login', types: 'task,comment', limit: '10' });
 */
export const SearchQuerySchema = z
  .object({
    /** Free text — min 2 chars after trim (avoids full-table LIKE scans on 1 char) */
    q: z.string().trim().min(2).max(200),
    /** CSV of entity types to search (task|list|folder|space|comment). Omit = all types. */
    types: csvSearchTypes.optional(),
    /** Per-type top-N to return (1–25, default 5). Applies to EACH group independently. */
    limit: z.coerce.number().int().min(1).max(25).default(5),
    /**
     * Opaque per-type continuation cursor from a group's `nextCursor`. A cursored request
     * pages exactly ONE group, so `types` must then hold a single value. Bounded to keep
     * the same explicit DoS invariants as `q`/`limit` (real cursors are ~100 chars).
     */
    cursor: z.string().max(500).optional(),
    /**
     * Narrow results to one workspace. Omit = all workspaces the user can access.
     * NOT an authorization input: the service ANDs this onto the scope predicate,
     * so it can only shrink the accessible set — an inaccessible workspaceId just
     * yields empty groups (same no-leak semantics as the resolve endpoint).
     */
    workspaceId: z.string().uuid().optional(),
    /**
     * ADR 0016: narrow results to one company. Same PURE-NARROWING, no-leak semantics
     * as `workspaceId` — ANDed onto the scope predicate, an inaccessible companyId
     * yields empty groups. For a NON-superadmin, results are ALWAYS constrained to the
     * caller's accessible companies regardless of this param. For a SUPERADMIN, OMITTING
     * companyId = all companies (the "All companies" merged view); the switcher always
     * sends the active companyId.
     */
    companyId: z.string().uuid().optional(),
  })
  .refine((v) => v.cursor === undefined || (v.types !== undefined && v.types.length === 1), {
    // Stable message KEY, not prose (Batch B7, docs/phase9/batch-b-i18n.md §2 Pattern A).
    message: 'search.cursorRequiresSingleType',
    path: ['cursor'],
  });

/** Type inferred from SearchQuerySchema (post-coercion: types[] and numeric limit) */
export type SearchQueryInput = z.infer<typeof SearchQuerySchema>;

/**
 * Ancestor context for a search hit — ids + display names of the containers ABOVE the
 * result, so the UI can render a breadcrumb without a second fetch. Only ancestors are
 * populated: a Space hit has no space ancestor (`spaceId` null), a List directly under a
 * Space has no folder (`folderId` null), etc. `taskId`/`taskTitle` are set for comments.
 * Payloads stay lean — no full entities, just what a breadcrumb needs.
 */
export const SearchBreadcrumbSchema = z.object({
  /**
   * Owning company (ADR 0016) — ALWAYS present (every hit resolves space→workspace→company,
   * so it is cheap and unconditional). Powers the per-row company badge in the superadmin
   * "All companies" merged view; the UI maps this id → CompanySummary via `user.companies`.
   */
  companyId: z.string().uuid(),
  /** Owning workspace (always present — everything lives in a workspace) */
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  /** Owning space (null when the result IS the space) */
  spaceId: z.string().uuid().nullable(),
  spaceName: z.string().nullable(),
  /** Owning folder (null when there is none — lists may sit directly under a space) */
  folderId: z.string().uuid().nullable(),
  folderName: z.string().nullable(),
  /** Owning list (null for space/folder/list hits; set for tasks and comments) */
  listId: z.string().uuid().nullable(),
  listName: z.string().nullable(),
  /** Owning task (set only for comment hits) */
  taskId: z.string().uuid().nullable(),
  taskTitle: z.string().nullable(),
});

/** Fields shared by every result variant (lean projection — never a full entity). */
const searchResultBase = {
  /** Entity id (task/list/folder/space/comment) */
  id: z.string().uuid(),
  /** Display text: task title, list/folder/space name, or a comment excerpt */
  title: z.string(),
  /** Last-updated timestamp (ISO 8601) — the primary ranking key */
  updatedAt: z.string().datetime(),
  /** Ancestor breadcrumb for navigation */
  breadcrumb: SearchBreadcrumbSchema,
} as const;

/**
 * One assignee on a task hit — the same `{ id, displayName, avatarUrl }` triple every other
 * avatar surface consumes, so `UserAvatar` renders it with no adapter.
 *
 * Deliberately NOT the full user: a search row needs a face and a name, never an email or a
 * role, and the base projection's "no full entities" rule applies to this join too.
 */
export const SearchAssigneeSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});

/**
 * A Task hit (matched on title or description).
 *
 * `assignees` (GHE #1085 follow-up) is the one field a task hit carries beyond the shared base:
 * the related-work picker asks the reader to choose between developments that frequently share
 * a title, and WHO worked on it is the discriminator a breadcrumb cannot supply. Ordered by
 * `displayName` so the same task always renders the same face first.
 *
 * ⚠️ OPTIONAL on purpose. A web build that ships ahead of the API reads `undefined` here, and a
 * required-typed array would then crash every consumer that maps over it — the exact skew that
 * `ui-permission-flags-must-fail-open` records. Consumers must treat absence as "no information",
 * never as "nobody is assigned".
 */
export const SearchTaskResultSchema = z.object({
  type: z.literal('task'),
  ...searchResultBase,
  assignees: z.array(SearchAssigneeSchema).optional(),
  /**
   * The task's dates and its status CATEGORY (GHE #1449), so the palette can show
   * «inicio → fin» and mark an overdue one with the shared `isTaskOverdue` rule — which needs
   * the category, because a done or cancelled task is never late.
   *
   * ⚠️ OPTIONAL for the same reason as `assignees`: `undefined` means an older API answered,
   * and must render nothing. `null` is a fact about the task (no date set).
   */
  startDate: z.string().datetime().nullable().optional(),
  dueDate: z.string().datetime().nullable().optional(),
  statusCategory: StatusCategoryEnum.optional(),
});
/** A List hit (matched on name). */
export const SearchListResultSchema = z.object({ type: z.literal('list'), ...searchResultBase });
/** A Folder hit (matched on name). */
export const SearchFolderResultSchema = z.object({
  type: z.literal('folder'),
  ...searchResultBase,
});
/** A Space hit (matched on name). */
export const SearchSpaceResultSchema = z.object({ type: z.literal('space'), ...searchResultBase });
/** A Comment hit (matched on content; `title` is a trimmed excerpt). */
export const SearchCommentResultSchema = z.object({
  type: z.literal('comment'),
  ...searchResultBase,
});

/** Discriminated union of all result variants (discriminant: `type`). */
export const SearchResultSchema = z.discriminatedUnion('type', [
  SearchTaskResultSchema,
  SearchListResultSchema,
  SearchFolderResultSchema,
  SearchSpaceResultSchema,
  SearchCommentResultSchema,
]);

// NOTE: inferred result/response/breadcrumb TYPES live in ../types/search.ts (single
// place, re-exported from the package `types` barrel). This file exports SCHEMAS + the
// query input type only, mirroring the TaskFilterInput convention and avoiding an
// ambiguous duplicate-name collision at the root `export *` barrel.

/**
 * One result group. `nextCursor` is this group's own continuation (null = end); pass it
 * back as `?types=<thisType>&cursor=<nextCursor>` to page just this group. There is
 * deliberately NO `total`/count field — per D1, counting LIKE matches is a second full
 * scan we refuse to pay at v1 scale.
 */
const searchGroup = <T extends z.ZodTypeAny>(
  item: T,
): z.ZodObject<{
  items: z.ZodArray<T>;
  nextCursor: z.ZodNullable<z.ZodString>;
}> =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });

/**
 * Response for GET /api/v1/search. Grouped-per-type under `data`; all five groups are
 * always present (a type excluded by `types`, or with no hits, yields empty items +
 * null cursor). `meta` echoes the effective query — no global cursor exists.
 * @example
 * SearchResponseSchema.parse({
 *   data: {
 *     tasks: { items: [], nextCursor: null },
 *     lists: { items: [], nextCursor: null },
 *     folders: { items: [], nextCursor: null },
 *     spaces: { items: [], nextCursor: null },
 *     comments: { items: [], nextCursor: null },
 *   },
 *   meta: { q: 'login', types: ['task', 'list', 'folder', 'space', 'comment'] },
 * });
 */
export const SearchResponseSchema = z.object({
  data: z.object({
    tasks: searchGroup(SearchTaskResultSchema),
    lists: searchGroup(SearchListResultSchema),
    folders: searchGroup(SearchFolderResultSchema),
    spaces: searchGroup(SearchSpaceResultSchema),
    comments: searchGroup(SearchCommentResultSchema),
  }),
  meta: z.object({
    /** Echo of the trimmed query string that was searched */
    q: z.string(),
    /** Effective types searched (all five when `types` was omitted) */
    types: z.array(SearchTypeEnum),
    /** Echo of the workspace narrowing filter, when one was applied */
    workspaceId: z.string().uuid().optional(),
    /** Echo of the company narrowing filter, when one was applied (ADR 0016) */
    companyId: z.string().uuid().optional(),
  }),
});
